"use client";

import { useMemo, useState } from "react";
import { ChevronDown, Lightbulb, Sparkles } from "lucide-react";
import { LearningRichContent } from "@/components/student/LearningRichContent";
import {
  LessonSectionFrame,
  lessonSectionBodyGapClass,
  lessonSectionBodyProseClass,
  stripSectionNumbering,
} from "@/components/student/LessonSectionFrame";
import { ensureMermaidInMarkdownFragment } from "@/lib/learning-content";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import ProfessionalRemedialLessonContent, {
  isProfessionalRemedialData,
  type ProfessionalRemedialLessonData,
} from "@/components/student/ProfessionalRemedialLessonContent";

export { isProfessionalRemedialData };
export type { ProfessionalRemedialLessonData };

export interface RemedialLessonData {
  title: string;
  lesson_type: string;
  course_level: string;
  career_goal: string;
  domain: string;
  opening: string;
  concepts: Array<{
    concept_index: number;
    concept_name: string;
    misconception_diagnosis: string;
    targeted_correction: string;
    clarification_box: string;
    technical_depth_box: string;
    diagram: string;
    pitfall_warning: string;
    domain_use_case: string;
    reflection_questions: string[];
    reflection_answers: string[];
  }>;
  closing: string;
}

interface RemedialLessonContentProps {
  data: RemedialLessonData | ProfessionalRemedialLessonData;
}

function isValidRemedialConcept(c: unknown): boolean {
  if (typeof c !== "object" || c === null) return false;
  const sec = c as Record<string, unknown>;
  return (
    typeof sec.concept_index === "number" &&
    typeof sec.concept_name === "string"
  );
}

export function isStructuredRemedialData(v: unknown): v is RemedialLessonData {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.title === "string" &&
    typeof o.lesson_type === "string" &&
    typeof o.course_level === "string" &&
    typeof o.career_goal === "string" &&
    typeof o.domain === "string" &&
    typeof o.opening === "string" &&
    Array.isArray(o.concepts) &&
    o.concepts.every(isValidRemedialConcept) &&
    typeof o.closing === "string"
  );
}

const REMEDIAL_DIRECTIVE_BLOCK_RE =
  /:::\s*([a-zA-Z_][\w-]*)(?:[ \t]+[^\n]+)?[ \t]*\n([\s\S]*?)\n:::/g;

function normalizeDirectiveEscapes(content: string): string {
  // Some remedial payloads arrive with JSON-escaped control chars; normalize so
  // :::directive blocks (especially :::mermaid) parse exactly like proactive.
  return normalizeGeneratedText(content);
}

function sanitizeRemedialMarkdownByLessonType(content: string, lessonTypeRaw: string): string {
  const normalizedContent = normalizeDirectiveEscapes(content);
  const lessonType = (lessonTypeRaw || "").trim().toLowerCase();

  // Remedial never uses platform strips.
  const alwaysForbidden = new Set(["platform"]);

  // Lesson-type specific restrictions for targeted correction blocks.
  const forbidTerminalAndCode =
    lessonType === "theory_only" || lessonType === "conceptual_ethics";

  // deployment_infra should keep code only for infra-oriented snippets.
  const deploymentAllowedCodeLangs = new Set(["yaml", "yml", "dockerfile"]);

  let strippedCodeSeen = false;
  let strippedTerminalSeen = false;

  const filtered = normalizedContent.replace(
    REMEDIAL_DIRECTIVE_BLOCK_RE,
    (full, rawTag: string, body: string) => {
      const tag = String(rawTag).toLowerCase();
      if (alwaysForbidden.has(tag)) return "";
      if (forbidTerminalAndCode && (tag === "terminal" || tag === "code" || tag === "output")) {
        if (tag === "terminal") strippedTerminalSeen = true;
        if (tag === "code") strippedCodeSeen = true;
        return "";
      }
      if (lessonType === "deployment_infra" && tag === "code") {
        const firstLine = full.split("\n")[0] ?? "";
        const langHint = firstLine.replace(/^:::\s*code/i, "").trim().toLowerCase();
        if (langHint && !deploymentAllowedCodeLangs.has(langHint)) {
          strippedCodeSeen = true;
          return "";
        }
      }
      return `:::${tag}\n${body.trim()}\n:::`;
    },
  );

  // Enforce rule: output appears only when code/terminal exists in the kept content.
  const hasTerminal = /:::\s*terminal(?:\s+[^\n]+)?\s*\n[\s\S]*?\n:::/i.test(filtered);
  const hasCode = /:::\s*code(?:\s+[^\n]+)?\s*\n[\s\S]*?\n:::/i.test(filtered);
  let result = filtered;
  if (!hasTerminal && !hasCode) {
    result = result.replace(/:::\s*output(?:\s+[^\n]+)?\s*\n[\s\S]*?\n:::/gi, "");
  }

  // Avoid raw fenced blocks in remedial display content.
  result = result.replace(/```[\s\S]*?```/g, "");

  if ((strippedCodeSeen || strippedTerminalSeen) && !result.includes(":::clarification")) {
    result +=
      "\n\n:::clarification [Format Note]\nSome command/code snippets were hidden for this lesson type.\n:::";
  }
  return result;
}

function joinConceptMarkdown(concept: RemedialLessonData["concepts"][number]): string {
  const diagramPart = ensureMermaidInMarkdownFragment(concept.diagram ?? "");
  const parts = [
    concept.misconception_diagnosis,
    concept.targeted_correction,
    concept.clarification_box,
    concept.technical_depth_box,
    diagramPart,
    concept.pitfall_warning,
    concept.domain_use_case,
  ].filter((p) => typeof p === "string" && p.trim().length > 0);
  return parts.join("\n\n");
}

function ReflectionBlock({
  conceptIndex,
  questions,
  answers,
  revealed,
  onToggle,
}: {
  conceptIndex: number;
  questions: string[];
  answers: string[];
  revealed: boolean;
  onToggle: () => void;
}) {
  const hasAnswers = answers.some((a) => typeof a === "string" && a.trim().length > 0);

  return (
    <section
      className="mt-6 overflow-hidden rounded-xl border border-indigo-200/90 bg-gradient-to-b from-indigo-50/50 via-white to-white shadow-sm ring-1 ring-indigo-100/40"
      aria-labelledby={`reflection-heading-${conceptIndex}`}
    >
      <div className="border-b border-indigo-100/90 bg-white/90 px-3 pb-2 pt-0">
        <div className="flex items-center gap-2.5">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm shadow-indigo-600/20"
            aria-hidden
          >
            <Lightbulb className="h-4 w-4" strokeWidth={2} />
          </div>
          <h3
            id={`reflection-heading-${conceptIndex}`}
            className="m-0 min-w-0 flex-1 text-[15px] font-semibold leading-tight tracking-tight text-slate-900"
          >
            Reflection questions
          </h3>
        </div>
        <div className="mt-1 max-w-none pl-[calc(2.25rem+0.625rem)] text-xs leading-snug text-slate-500 my-2">
          Pause and answer in your own words. Reveal suggested responses when you are ready.
        </div>
      </div>

      <ul className="list-none space-y-2 px-3 pb-2 pt-0 ml-1">
        {questions.map((q, i) => (
          <li
            key={i}
            className="flex gap-2.5 rounded-lg border border-slate-100 bg-slate-50/80 px-2.5 py-2 transition-colors hover:border-indigo-100 hover:bg-indigo-50/25"
          >
            <span className="my-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-bold tabular-nums text-indigo-800">
              {i + 1}
            </span>
            <div className="min-w-0 text-sm leading-snug py-2 text-slate-700">{q}</div>
          </li>
        ))}
      </ul>

      {hasAnswers && (
        <div className="border-t border-indigo-100/80 bg-slate-50/40 px-3 py-0 pb-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={revealed}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-800 shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50/80 active:scale-[0.99]"
          >
            <Sparkles className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
            <span className="flex-1 text-center">
              {revealed ? "Hide suggested responses" : "Reveal suggested responses"}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-indigo-600 transition-transform duration-200 ${revealed ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>

          {revealed && (
            <div
              className="mt-2.5 space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2.5 shadow-inner"
              role="region"
              aria-label="Suggested responses"
            >
              
              <ul className="list-none space-y-2 ml-1">
                {answers.map((a, i) => {
                  const text = typeof a === "string" ? a.trim() : "";
                  if (!text) return null;
                  return (
                    <li
                      key={i}
                      className="rounded-md border border-indigo-100/90 bg-white/90 px-2.5 py-2 text-sm leading-snug text-slate-700 shadow-sm ml-1 mt-2"
                    >
                      {text}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function RemedialLessonContent({ data }: RemedialLessonContentProps) {
  const [revealedAnswers, setRevealedAnswers] = useState<Record<number, boolean>>({});

  // Must be declared before any conditional returns (Rules of Hooks)
  const concepts = useMemo(() => {
    if (isProfessionalRemedialData(data)) return [];
    const d = data as RemedialLessonData;
    return [...d.concepts].sort((a, b) => a.concept_index - b.concept_index);
  }, [data]);

  if (isProfessionalRemedialData(data)) {
    return <ProfessionalRemedialLessonContent data={data} />;
  }

  return (
    <article className="learning-content max-w-none prose prose-gray max-w-none">
      <section className="mb-8 not-prose rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center rounded-full bg-indigo-600 text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
            Before we begin
          </span>
        </div>
        <LearningRichContent
          content={data.opening}
          className={`${lessonSectionBodyProseClass} text-sm`}
          segmentGapClassName={lessonSectionBodyGapClass}
        />
      </section>

      <div className="space-y-8 not-prose">
        {concepts.map((concept, idx) => {
          const bodyMd = sanitizeRemedialMarkdownByLessonType(
            joinConceptMarkdown(concept),
            data.lesson_type,
          );
          return (
            <LessonSectionFrame
              key={`${concept.concept_index}-${concept.concept_name}`}
              sectionNumber={concept.concept_index}
              heading={stripSectionNumbering(concept.concept_name)}
            >
              <LearningRichContent
                content={bodyMd}
                className={lessonSectionBodyProseClass}
                segmentGapClassName={lessonSectionBodyGapClass}
              />

              {(concept.reflection_questions?.length ?? 0) > 0 && (
                <ReflectionBlock
                  conceptIndex={concept.concept_index}
                  questions={concept.reflection_questions}
                  answers={concept.reflection_answers ?? []}
                  revealed={!!revealedAnswers[idx]}
                  onToggle={() =>
                    setRevealedAnswers((prev) => ({ ...prev, [idx]: !prev[idx] }))
                  }
                />
              )}
            </LessonSectionFrame>
          );
        })}
      </div>

      <section className="mt-10 rounded-xl border border-slate-200 bg-white p-5 not-prose">
        <LearningRichContent
          content={data.closing}
          className={lessonSectionBodyProseClass}
          segmentGapClassName={lessonSectionBodyGapClass}
        />
      </section>
    </article>
  );
}
